'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { Users, Star, Briefcase, GraduationCap } from 'lucide-react';

const opportunities = [
  {
    icon: GraduationCap,
    title: 'Applications Open',
    desc: 'REUSA is now accepting applications from aspiring and practising real estate professionals across South Africa and the continent.',
  },
  {
    icon: Star,
    title: 'Early Applicant Opportunities',
    desc: 'Founding cohort members will have access to exclusive early-entry benefits, direct engagement with faculty, and a permanent place in the REUSA story.',
  },
  {
    icon: Briefcase,
    title: 'Corporate Sponsorship Opportunities',
    desc: 'Organisations that invest in their people through REUSA become part of building the next generation of African real estate professionals.',
  },
  {
    icon: Users,
    title: 'Industry Mentorship Opportunities',
    desc: 'Experienced professionals are invited to contribute their knowledge and networks to the students who will shape the future of the industry.',
  },
];

export default function FoundingCohortSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const els = ref?.current?.querySelectorAll('.section-reveal, .stagger-child');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-24 bg-primary relative overflow-hidden" ref={ref}>
      {/* Gold top line */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #c9a84c 30%, #e8c96a 50%, #c9a84c 70%, transparent 100%)' }} />
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        {/* Header */}
        <div className="text-center mb-16 section-reveal">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-accent/60" />
            <span className="font-sans text-[0.65rem] font-semibold tracking-widest uppercase text-accent/80">Founding Generation</span>
            <div className="h-px w-12 bg-accent/60" />
          </div>
          <h2 className="font-serif text-white mb-5" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1.15, letterSpacing: '-0.015em' }}>
            Become Part Of REUSA&apos;s<br />
            <span className="italic text-accent">Founding Generation</span>
          </h2>
          <p className="font-sans text-white/60 max-w-2xl mx-auto leading-relaxed text-base">
            Join the next generation of property professionals helping shape the future of African real estate.
            The founding cohort is not just the first class — it is the foundation of an institution.
          </p>
        </div>

        {/* Opportunity cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {opportunities?.map((item, i) => (
            <div
              key={item?.title}
              className="stagger-child bg-white/5 border border-white/10 rounded-2xl p-7 group hover:bg-white/8 hover:border-accent/30 transition-all duration-300"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-11 h-11 rounded-xl bg-white/8 border border-white/10 flex items-center justify-center mb-5 group-hover:bg-accent/20 group-hover:border-accent/30 transition-all duration-300">
                <item.icon size={20} className="text-accent" />
              </div>
              <h3 className="font-serif font-semibold text-white text-base mb-3 leading-tight">{item?.title}</h3>
              <p className="font-sans text-sm text-white/55 leading-relaxed">{item?.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center section-reveal">
          <div className="inline-flex flex-col sm:flex-row gap-4 items-center justify-center">
            <Link
              href="/apply"
              className="btn-gold px-10 py-4 rounded-xl font-semibold text-sm"
            >
              Apply to the Founding Cohort
            </Link>
            <Link
              href="/corporate-training"
              className="font-sans text-sm font-semibold text-white/60 hover:text-white transition-colors border-b border-white/20 hover:border-white pb-0.5"
            >
              Corporate Sponsorship Enquiries
            </Link>
          </div>
          <p className="font-sans text-xs text-white/35 mt-6 max-w-md mx-auto leading-relaxed">
            Founding cohort members become a permanent part of the REUSA story — the professionals who believed in the institution before it was built.
          </p>
        </div>
      </div>
    </section>
  );
}
